import { create } from 'zustand';

/**
 * RIM-049 / RIM-114 - Estado transitorio da atividade do Rimati.
 * Guarda apenas o que a UI precisa mostrar enquanto acontece; o historico
 * definitivo e a Timeline (Activity). Nao e fonte de verdade de nada.
 */
export interface AgentActivityEntry {
  executionId: string;
  /** Rotulo em linguagem de negocio, ex.: "Atualizando contato...". */
  label: string;
  at: number;
}

interface AgentActivityState {
  /** Conversas em que o assistente esta processando agora. */
  processing: Record<string, { executionId: string; agentName: string }>;
  /** Ultima acao observavel por conversa. */
  lastAction: Record<string, AgentActivityEntry>;
  /** Falha operacional visivel no ambiente demo/dev. */
  failures: Record<string, string>;
  /** Oportunidades que acabaram de mudar de etapa (para a animacao). */
  recentlyMoved: Record<string, number>;

  startProcessing: (conversationId: string, executionId: string, agentName: string) => void;
  setAction: (conversationId: string, executionId: string, label: string) => void;
  stopProcessing: (conversationId: string) => void;
  setFailure: (conversationId: string, reason: string) => void;
  markMoved: (opportunityId: string) => void;
  clearMoved: (opportunityId: string) => void;
}

export const useAgentActivityStore = create<AgentActivityState>((set) => ({
  processing: {},
  lastAction: {},
  failures: {},
  recentlyMoved: {},

  startProcessing: (conversationId, executionId, agentName) =>
    set((state) => ({
      processing: { ...state.processing, [conversationId]: { executionId, agentName } },
      failures: withoutKey(state.failures, conversationId),
    })),

  setAction: (conversationId, executionId, label) =>
    set((state) => ({
      lastAction: {
        ...state.lastAction,
        [conversationId]: { executionId, label, at: Date.now() },
      },
    })),

  stopProcessing: (conversationId) =>
    set((state) => ({
      processing: withoutKey(state.processing, conversationId),
      lastAction: withoutKey(state.lastAction, conversationId),
    })),

  setFailure: (conversationId, reason) =>
    set((state) => ({
      processing: withoutKey(state.processing, conversationId),
      lastAction: withoutKey(state.lastAction, conversationId),
      failures: { ...state.failures, [conversationId]: reason },
    })),

  markMoved: (opportunityId) =>
    set((state) => ({
      recentlyMoved: { ...state.recentlyMoved, [opportunityId]: Date.now() },
    })),

  clearMoved: (opportunityId) =>
    set((state) => ({ recentlyMoved: withoutKey(state.recentlyMoved, opportunityId) })),
}));

function withoutKey<T>(source: Record<string, T>, key: string): Record<string, T> {
  if (!(key in source)) return source;
  const copy = { ...source };
  delete copy[key];
  return copy;
}
